import DataGrid, {
  Column,
  ColumnChooser,
  HeaderFilter,
  Item,
  Lookup,
  MasterDetail,
  Paging,
  Scrolling,
  SearchPanel,
  Toolbar,
  type DataGridRef
} from 'devextreme-react/data-grid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RowPreparedEvent } from 'devextreme/ui/data_grid';
import { ActiveUsersBar } from '../realtime/ActiveUsersBar';
import { getActivePresenceAvatarColor } from '../realtime/presenceAvatarColors';
import { OPS_SITE_GRID_PROPS, OpsGridStateStoring } from '../opsDataGrid';
import { PatientFormPopup } from './PatientFormPopup';
import { usePatientTrackingCsvImport } from './PatientTrackingCsvImportButton';
import { PatientTrackingGridToolbarItems } from './PatientTrackingGridToolbar';
import { patientTrackingApi } from './patientTrackingApi';
import { useLiveRows } from './useLiveRows';
import {
  PATIENT_STATUS_LABELS,
  PatientTrackingBoardKeys,
  type CoveredEntityDto,
  type OurPharmacyDto,
  type PatientDto,
  type PatientTrackingCsvImportConfig,
  type TrackerEntryDto
} from './patientTrackingTypes';
import './patientTracking.css';

const PATIENTS_GRID_SCHEMA_VERSION = 1;
const PATIENTS_GRID_COLUMNS = [
  'lastName',
  'firstName',
  'pharmacyName',
  'dob',
  'phone',
  'email',
  'homeCoveredEntityId',
  'enrollmentForm',
  'openIssues',
  'totalIssues',
  'isActive',
  'city',
  'state',
  'postalCode',
  'modified',
  'modifiedBy'
] as const;

export interface PatientsGridProps {
  coveredEntities: CoveredEntityDto[];
  pharmacies: OurPharmacyDto[];
  currentUserId: string;
  currentUserName?: string;
  currentUserAliases?: Iterable<string>;
  csvImport?: PatientTrackingCsvImportConfig;
  stateStorageKey?: string;
}

/**
 * Master patients grid. Real-time presence + per-row edit lock, soft duplicate warning on save,
 * and a DevExtreme master-detail row that lists this patient's tracker issues (loaded from a
 * cached tracker fetch on first expand and kept live by the tracker board's row push).
 */
export function PatientsGrid({
  coveredEntities,
  pharmacies,
  currentUserId,
  currentUserName = 'User',
  currentUserAliases,
  csvImport,
  stateStorageKey = 'patients-grid'
}: PatientsGridProps) {
  const {
    rows,
    loading,
    error,
    reload,
    applyUpsert,
    applyRemove,
    activeBarRoster,
    rowEditors,
    beginEditRow,
    endEditRow
  } = useLiveRows<PatientDto>(PatientTrackingBoardKeys.patients, patientTrackingApi.listPatients);

  const gridRef = useRef<DataGridRef>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [formViewMode, setFormViewMode] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Tracker entries for the master-detail; loaded once on demand and kept live via the tracker board.
  const trackerLive = useLiveRows<TrackerEntryDto>(
    PatientTrackingBoardKeys.tracker,
    patientTrackingApi.listTrackerEntries
  );

  const csvImportUi = usePatientTrackingCsvImport({
    config: csvImport,
    disabled: loading,
    onImported: () => {
      void reload();
      void trackerLive.reload();
    }
  });

  const ceOptions = useMemo(
    () => coveredEntities.map((c) => ({ id: c.id, name: c.coveredEntityName })),
    [coveredEntities]
  );

  const editing = useMemo(
    () => (editingId ? rows.find((r) => r.id === editingId) ?? null : null),
    [editingId, rows]
  );

  const openCreate = () => {
    setEditingId(null);
    setFormViewMode(false);
    setEditorOpen(true);
  };

  const openView = useCallback((row: PatientDto) => {
    setEditingId(row.id);
    setFormViewMode(true);
    setEditorOpen(true);
  }, []);

  const enterEditMode = useCallback(() => {
    if (!editingId) return;
    const lock = rowEditors.get(editingId);
    if (lock && lock.userId.toLowerCase() !== currentUserId.toLowerCase()) {
      window.alert(`${lock.displayName} is currently editing this patient.`);
      return;
    }
    setFormViewMode(false);
    beginEditRow(editingId);
  }, [beginEditRow, currentUserId, editingId, rowEditors]);

  const closeEditor = useCallback(() => {
    setEditorOpen(false);
    if (editingId && !formViewMode) endEditRow();
    setFormViewMode(false);
  }, [editingId, endEditRow, formViewMode]);

  const onSaved = (patient: PatientDto) => {
    applyUpsert(patient);
  };

  const onDeleted = (id: string) => {
    applyRemove(id);
  };

  const onRowPrepared = (e: RowPreparedEvent<PatientDto, string>) => {
    if (e.rowType !== 'data' || !e.data) return;
    const editor = rowEditors.get(e.data.id);
    if (editor && editor.userId.toLowerCase() !== currentUserId.toLowerCase()) {
      const color = getActivePresenceAvatarColor(editor.userId);
      e.rowElement.classList.add('patient-tracker-row--locked');
      e.rowElement.style.setProperty('--pt-lock-color', color);
      e.rowElement.title = `Being edited by ${editor.displayName}`;
    }
  };

  return (
    <>
      <DataGrid
            ref={gridRef}
            dataSource={rows}
            keyExpr="id"
            height="100%"
            width="100%"
            showBorders
            columnAutoWidth
            columnHidingEnabled
            allowColumnResizing
            allowColumnReordering
            columnResizingMode="widget"
            {...OPS_SITE_GRID_PROPS}
            noDataText={error ?? (loading ? 'Loading…' : 'No patients yet.')}
            onRowDblClick={(e) => {
              e.event?.stopPropagation();
              if (e.data) openView(e.data);
            }}
            onRowPrepared={onRowPrepared}
          >
            <OpsGridStateStoring
              storageKey={stateStorageKey}
              schemaVersion={PATIENTS_GRID_SCHEMA_VERSION}
              columns={PATIENTS_GRID_COLUMNS}
            />
            <Toolbar>
              <Item location="before">
                <div className="patient-grid-toolbar-presence">
                  <ActiveUsersBar
                    roster={activeBarRoster}
                    currentUserId={currentUserId}
                    editingLabel="Editing:"
                  />
                  {rowEditors.size > 0 ? (
                    <span className="patient-tracker-page__lock-note">
                      {rowEditors.size} row(s) being edited
                    </span>
                  ) : null}
                </div>
              </Item>
              <PatientTrackingGridToolbarItems
                gridRef={gridRef}
                loading={loading}
                onRefresh={() => {
                  void reload();
                  void trackerLive.reload();
                }}
                newItem={{ hint: 'Add patient', onClick: openCreate }}
                uploadSlot={csvImportUi.uploadToolbarItem}
                uploadMenuItem={csvImportUi.uploadMenuItem}
                export={{ fileName: 'patients', worksheetName: 'Patients', hint: 'Export patients' }}
              />
              <Item name="searchPanel" location="after" />
            </Toolbar>
            <SearchPanel visible width={260} placeholder="Search patients…" />
            <HeaderFilter visible />
            <ColumnChooser enabled mode="select" allowSearch title="Choose columns" />
            <Scrolling mode="standard" scrollByThumb showScrollbar="onScroll" useNative />
            <Paging defaultPageSize={50} />

            <Column dataField="lastName" caption="Last name" minWidth={140} />
            <Column dataField="firstName" caption="First name" minWidth={140} />
            <Column dataField="pharmacyName" caption="Pharmacy" minWidth={160} />
            <Column dataField="dob" caption="DOB" dataType="date" width={120} />
            <Column dataField="phone" caption="Phone" width={140} />
            <Column dataField="email" caption="Email" minWidth={180} visible={false} />
            <Column dataField="homeCoveredEntityId" caption="Home CE" minWidth={160}>
              <Lookup dataSource={ceOptions} valueExpr="id" displayExpr="name" />
            </Column>
            <Column dataField="enrollmentForm" caption="Enroll. Form" dataType="boolean" width={110} />
            <Column dataField="openIssues" caption="Open" dataType="number" width={80} />
            <Column dataField="totalIssues" caption="Total" dataType="number" width={80} />
            <Column dataField="isActive" caption="Active" dataType="boolean" width={90} visible={false} />
            <Column dataField="city" caption="City" minWidth={120} visible={false} />
            <Column dataField="state" caption="State" width={80} visible={false} />
            <Column dataField="postalCode" caption="Postal" width={90} visible={false} />
            <Column dataField="modified" caption="Modified" dataType="datetime" width={160} visible={false} />
            <Column dataField="modifiedBy" caption="Modified By" minWidth={140} visible={false} />

            <MasterDetail enabled component={(props) => (
              <PatientIssuesDetail
                patient={props.data.data as PatientDto}
                entries={trackerLive.rows}
                loading={trackerLive.loading}
                onReload={() => void trackerLive.reload()}
              />
            )} />
          </DataGrid>

      <PatientFormPopup
        visible={editorOpen}
        patient={editing}
        viewMode={formViewMode}
        onRequestEdit={enterEditMode}
        pharmacies={pharmacies}
        coveredEntities={coveredEntities}
        currentUserId={currentUserId}
        currentUserName={currentUserName}
        currentUserAliases={currentUserAliases}
        onClose={closeEditor}
        onSaved={onSaved}
        onDeleted={onDeleted}
      />

      {csvImportUi.importPopup}
    </>
  );
}

interface PatientIssuesDetailProps {
  patient: PatientDto;
  entries: TrackerEntryDto[];
  loading: boolean;
  onReload: () => void;
}

function PatientIssuesDetail({ patient, entries, loading, onReload }: PatientIssuesDetailProps) {
  useEffect(() => {
    if (!loading && entries.length === 0) onReload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mine = entries.filter((e) => e.patientId === patient.id);

  return (
    <div className="patient-issues-detail">
      <div className="patient-issues-detail__title">
        Tracker issues for {patient.fullName} ({mine.length})
      </div>
      {mine.length === 0 ? (
        <div className="patient-tracking-muted-text">No tracker issues for this patient yet.</div>
      ) : (
        <DataGrid
          dataSource={mine}
          keyExpr="id"
          showBorders
          columnAutoWidth
          rowAlternationEnabled
        >
          <Column dataField="status" caption="Status" width={120}
            calculateDisplayValue={(row: TrackerEntryDto) => PATIENT_STATUS_LABELS[row.status]} />
          <Column dataField="coveredEntityName" caption="CE" minWidth={140} />
          <Column dataField="currentIssue" caption="Current issue" minWidth={260} />
          <Column dataField="emailsSent" caption="Emails" dataType="number" width={80} />
          <Column dataField="modified" caption="Modified" dataType="datetime" width={160} />
        </DataGrid>
      )}
    </div>
  );
}
